import React from 'react';
import { Check, MoreHorizontal, Pencil, Save, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export default function SettingRow({
  label,
  description,
  value,
  configured = true,
  editing = false,
  canEdit = true,
  dirty = true,
  saving = false,
  saved = false,
  error = '',
  editor,
  onEdit,
  onSave,
  onCancel,
  onRemove,
  editLabel = 'Modifica',
  emptyLabel = 'Non configurato',
  removeLabel = 'Rimuovi configurazione',
  compact = false,
  testId,
}) {
  return (
    <div className={`border-b border-stone-100 last:border-b-0 ${compact ? 'px-3 py-1.5' : 'px-4 py-4 sm:px-5'}`} data-testid={testId}>
      <div className={`flex gap-3 sm:items-center sm:justify-between ${compact ? 'items-center' : 'flex-col sm:flex-row sm:gap-5'}`}>
        <div className="min-w-0 flex-1">
          <div className={`font-semibold text-stone-800 ${compact ? 'text-sm' : ''}`}>{label}</div>
          {editing ? (
            description && <div className="mt-1 text-xs text-stone-500">{description}</div>
          ) : (
            <div className={`${compact ? 'mt-0.5 text-xs' : 'mt-1 text-sm'} ${configured ? 'text-brand-navy' : 'text-stone-500'}`}>
              {configured ? value : emptyLabel}
            </div>
          )}
          {error && (
            <div
              className="mt-2 text-xs font-medium text-red-700"
              id={testId ? `${testId}-error` : undefined}
              role="alert"
            >
              {error}
            </div>
          )}
        </div>

        {editing ? (
          <div className={`flex items-center justify-end gap-2 ${compact ? 'shrink-0' : 'w-full sm:w-auto'}`}>
            <div className="min-w-0 flex-1 sm:flex-none">{editor}</div>
            <Button type="button" size="sm" disabled={!canEdit || !dirty || saving} onClick={onSave}>
              <Save className="mr-1.5 h-4 w-4" aria-hidden="true" /> <span className={compact ? 'sr-only' : ''}>Salva</span>
            </Button>
            <Button type="button" size="icon" variant="outline" disabled={saving} onClick={onCancel} aria-label={`Annulla modifica ${label}`}>
              <X className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        ) : (
          <div className={`flex items-center justify-end gap-1.5 ${compact ? 'shrink-0' : 'flex-wrap gap-2'}`}>
            {configured && (
              <span className={`inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 font-bold uppercase tracking-wide text-emerald-700 ${compact ? 'px-1.5 py-0.5 text-[9px]' : 'px-2.5 py-1 text-[10px]'}`}>
                <Check className="h-3 w-3" aria-hidden="true" /> Configurato
              </span>
            )}
            {saved && <span className="text-xs font-semibold text-emerald-700" role="status">✓ Salvato</span>}
            {canEdit && (
              <Button
                type="button"
                size="sm"
                variant={configured ? 'outline' : 'default'}
                onClick={onEdit}
                disabled={saving}
                data-testid={testId ? `${testId}-edit` : undefined}
              >
                {configured && <Pencil className="mr-1.5 h-4 w-4" aria-hidden="true" />}
                {!configured && <span className="mr-1.5 text-base leading-none" aria-hidden="true">+</span>}
                {configured ? editLabel : 'Imposta'}
              </Button>
            )}
            {onRemove && configured && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button type="button" size="icon" variant="ghost" disabled={saving} aria-label={`Azioni per ${label}`}>
                    <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    className="text-red-600 focus:bg-red-50 focus:text-red-700"
                    onSelect={onRemove}
                    data-testid={testId ? `${testId}-remove` : undefined}
                  >
                    <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" /> {removeLabel}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        )}
      </div>
    </div>
  );
}